import React, { useEffect, useState } from "react";
import { Link, useLocation, useHistory } from "react-router-dom";
import { reverse } from "named-urls";
import { Table, Space, PageHeader } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useInjectReducer, useInjectSaga } from "redux-injectors";
import { sliceKey, reducer, actions } from "./slice";
import { userListingSaga } from "./saga";
import * as selectors from "./selectors";
import routes from "../../../../../routes";
import { useAuth } from "../../../../contexts/auth";

const { Column } = Table;
const STORAGE_KEY = 'userSummaryFilters';

const UserSummaryListing = () => {
  const auth = useAuth();
  const location = useLocation();
  const history = useHistory();
  const user = auth.loggedInUser;
  const userRole = user.role;
  useInjectReducer({ key: sliceKey, reducer: reducer });
  useInjectSaga({ key: sliceKey, saga: userListingSaga });
  const dispatch = useDispatch();

  // Initialize state from sessionStorage
  const getInitialState = () => {
    try {
      const saved = sessionStorage.getItem(STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Failed to parse saved filters:', e);
    }
    return { page: 1, companyName: null };
  };

  const initialState = getInitialState();
  const [currentPage, setCurrentPage] = useState(initialState.page);
  const [companyNameFilter, setCompanyNameFilter] = useState(initialState.companyName);

  const summaryData = useSelector(selectors.selectSummaryData) || [];
  const totalCompanies = useSelector(selectors.selectTotalCompanies);
  const totalCompanyCount = useSelector(selectors.selectCompanyCount);

  // Save state to sessionStorage whenever it changes
  useEffect(() => {
    const state = { page: currentPage, companyName: companyNameFilter };
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch (e) {
      console.error('Failed to save filters:', e);
    }
  }, [currentPage, companyNameFilter]);

  // Fetch data when filters change
  useEffect(() => {
    let query = `page=${currentPage}&limit=10`;
    if (companyNameFilter) query += `&companyName=${companyNameFilter}`;
    
    dispatch(actions.fetchUserSummary({ query }));
  }, [dispatch, currentPage, companyNameFilter]);

  // Fetch total companies on mount
  useEffect(() => {
    dispatch(actions.fetchTotalCompanies());
  }, [dispatch]);

  const companyData = summaryData.map((company) => ({
    key: company._id,
    id: company._id,
    company: company.companyName,
    adminCount: company.users.filter((user) => user.role === "company_admin")
      .length,
    userCount: company.users.filter((user) => user.role === "company_user")
      .length,
  }));

  const getCompanyFilters = () => {
    if (!totalCompanies) return [];
    return totalCompanies
      .map((entity) => ({
        text: entity.companyName,
        value: entity.companyName,
      }))
      .sort((a, b) => a.text.localeCompare(b.text));
  };

  const handleTablePagination = (pagination, filters) => {
    setCurrentPage(pagination.current);
    setCompanyNameFilter(filters.company?.[0] || null);
  };

  return (
    <div style={{ height: "100%" }}>
      <PageHeader ghost={false} title="User Accounts Summary" />
      <div
        style={{
          height: "calc(100vh - 137px)",
          overflowY: "auto",
          padding: 24,
        }}
      >
        <Table
          dataSource={companyData}
          onChange={handleTablePagination}
          pagination={{
            current: currentPage,
            pageSize: 10,
            total: totalCompanyCount,
            showSizeChanger: false,
            disabled: companyData.length === 0,
          }}
        >
          <Column
            title="Company Name"
            dataIndex="company"
            key="company"
            filters={getCompanyFilters()}
            filterSearch={true}
            filteredValue={companyNameFilter ? [companyNameFilter] : null}
            width="30%"
          />
          <Column title="Users" dataIndex="userCount" key="userCount" />
          <Column title="Admins" dataIndex="adminCount" key="adminCount" />
          <Column
            title="Action"
            key="action"
            width="25%"
            render={(text, record) => (
              <Space size="middle">
                <Link
                  to={{
                    pathname: reverse(
                      routes.backOffice.companies.details.users.list,
                      {
                        companyId: record.id,
                      }
                    ),
                    search: location.search,
                  }}
                >
                  View Users
                </Link>
                {userRole !== "support_team" && (
                  <Link
                    to={{
                      pathname: reverse(
                        routes.backOffice.companies.details.users.new,
                        {
                          companyId: record.id,
                        }
                      ),
                      search: location.search,
                    }}
                  >
                    Add User
                  </Link>
                )}
              </Space>
            )}
          />
        </Table>
      </div>
    </div>
  );
};

export default UserSummaryListing;